import React, { useEffect, useState } from "react";
import { Ticket, Tag, Star } from "lucide-react";
import { api } from "../../redux/apis/api";

// Read-only reversal-state card for return VIEW mode. Shows which of the
// ORIGINATING bill's benefits (scheme / coupon / loyalty points) have been
// reversed by returns — derived from the reversed flags on
// GET /order/bill-benefits/:sot_id (benefits reverse all-or-nothing on the
// completing return). Card styling mirrors
// invoice-section/used-benefits-summary.js.

const fmtINR = (n) => Number(n || 0).toFixed(2);

// tone: "done" (green) only for CONFIRMED states; "warn" (amber) for
// attempted-but-unconfirmed; neutral grey otherwise. Points reversal is
// confirmed by the ledger write (icn_points_recredited), never inferred from
// returned quantities alone (review blocker: the RPC is best-effort, so a
// full return does not prove the customer's balance was restored).
const StatusPill = ({ tone, text }) => (
  <span
    className={`text-[11px] font-bold uppercase tracking-wider rounded px-2 py-0.5 border shrink-0 ${
      tone === "done"
        ? "text-[#15803C] bg-[#F0FBF4] border-[#BBE7CC]"
        : tone === "warn"
        ? "text-[#B45309] bg-[#FFFBEB] border-[#FDE68A]"
        : "text-[#64748B] bg-[#F8FAFC] border-[#E2E8F0]"
    }`}
  >
    {text}
  </span>
);

const Card = ({ icon, iconBg, label, labelColor, title, amount, status }) => (
  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 mb-3 flex items-center gap-3">
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
      style={{ background: iconBg, color: "white" }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-[11px] uppercase tracking-wider font-bold"
        style={{ color: labelColor }}
      >
        {label}
      </div>
      <div className="text-[14px] font-bold text-[#0F172A] mt-0.5 flex items-center gap-2 flex-wrap">
        {title && <span className="font-mono tracking-wide">{title}</span>}
        <span className="text-[13px] text-[#475569]">− ₹{fmtINR(amount)}</span>
      </div>
    </div>
    {status}
  </div>
);

const BenefitReversalSummary = ({ sotId }) => {
  const [benefits, setBenefits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sotId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/order/bill-benefits/${sotId}`)
      .then((res) => {
        if (!cancelled) setBenefits(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setBenefits(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sotId]);

  if (loading) return null;

  const { scheme, coupon, points } = benefits || {};
  if (!scheme && !coupon && !points) return null;

  return (
    <div className="my-3">
      <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-bold mb-2">
        Original bill benefits — reversal state
      </div>
      {scheme && (
        <Card
          icon={<Tag size={18} />}
          iconBg="linear-gradient(135deg, #8B5CF6, #6D28D9)"
          label="Scheme"
          labelColor="#6D28D9"
          title={scheme.name || "Scheme"}
          amount={scheme.amount}
          status={
            <StatusPill
              tone={scheme.reversed === true ? "done" : "neutral"}
              text={scheme.reversed === true ? "Restored" : "Not reversed"}
            />
          }
        />
      )}
      {coupon && (
        <Card
          icon={<Ticket size={18} />}
          iconBg="linear-gradient(135deg, #22C55E, #15803C)"
          label="Coupon"
          labelColor="#15803C"
          title={coupon.code || "Coupon"}
          amount={coupon.amount}
          status={
            <StatusPill
              tone={coupon.reversed === true ? "done" : "neutral"}
              text={coupon.reversed === true ? "Freed for reuse" : "Still used"}
            />
          }
        />
      )}
      {points && (
        <Card
          icon={<Star size={18} />}
          iconBg="linear-gradient(135deg, #F59E0B, #B45309)"
          label="Loyalty points"
          labelColor="#B45309"
          title={points.points != null ? `${points.points} pts` : null}
          amount={points.amount}
          status={
            <StatusPill
              // Green ONLY on reversal_confirmed (ledger-verified). Old
              // backends don't send it — their inferred `reversed: true`
              // deliberately lands on the amber unconfirmed state (safe
              // during version skew), never on a false success.
              tone={
                points.reversal_confirmed === true
                  ? "done"
                  : points.reversal_inferred === true || points.reversed === true
                  ? "warn"
                  : "neutral"
              }
              text={
                points.reversal_confirmed === true
                  ? "Re-credited"
                  : points.reversal_inferred === true || points.reversed === true
                  ? "Re-credit unconfirmed — verify balance"
                  : "Not re-credited"
              }
            />
          }
        />
      )}
    </div>
  );
};

export default BenefitReversalSummary;
